import React, { ChangeEvent, useEffect, useMemo } from "react";
import _ from 'lodash';
import cx from 'classnames';
import pluralize from 'pluralize';

import { Collection } from "../../../../types/collections";

import styles from "./card.module.scss";
import Thumbnail from "../../../../components/thumbnail";
import { Media } from "../../../../types/media";
import { useDraggable } from "@dnd-kit/core";
import { useSelector } from "react-redux";
import Tooltip from "../../../../components/tooltip";
import LinesEllipsis from 'react-lines-ellipsis';
import { NavigateEntity } from "src/components/NavigateEntity";

interface CollectionCardProps {
  item: Collection;
  editMode?: 'create' | 'rename' | 'selection' | 'clone' | 'delete' | null | undefined;
  selected?: boolean | null | undefined;
  readonly?: boolean;
  // eslint-disable-next-line no-unused-vars
  onEditChanges?: (value: any) => void;
  // eslint-disable-next-line no-unused-vars
  onClick?: (collection: Collection) => void;
}

const CollectionCard: React.FC<CollectionCardProps> = ({ item, editMode, selected, readonly, onEditChanges, onClick }) => {
  const renaming = editMode === 'rename';
  const selection = _.includes(['clone', 'delete', 'selection'], editMode);
  const cloning = editMode === 'clone';

  const [updatedName, setUpdatedName] = React.useState(item.name);

  // using touch events to ensure onClick functionality works on touch devices
  const [wasTouchMoved, setWasTouchMoved] = React.useState(false);

  const name = _.get(item, 'name') || "";
  const icon = _.get(item, 'banner.background') as Media;
  const internal = _.get(item, 'internal', false);
  const live = _.get(item, 'live', false);
  const template = _.get(item, 'collectionTemplate', false);

  const defaultImage = item.id === 'uncategorized' ? '/images/collections/collection_uncategorized.jpeg' : '/images/collections/background.jpeg';

  const count = useMemo(() => {
    const sectionsCount = _.get(item, 'sectionsCount', 0);
    if (sectionsCount > 0) {
      return `${sectionsCount} ${pluralize('Sections', sectionsCount)}`;
    }

    const assetsCount = _.get(item, 'assetsCount', 0);
    return `${assetsCount} ${pluralize('Assets', assetsCount)}`;
  }, [item]);

  const renamed = useMemo(() => {
    return updatedName !== name;
  }, [name, updatedName]);

  const moving = useSelector((state: any) => _.get(state, `collections.move['${item.id}'].moving`, false));

  const disabled = useMemo(() => {
    if (readonly) {
      return true;
    }

    if (cloning && item.dealRoom) {
      return true;
    }

    if (!cloning && renaming) {
      return !item.canEdit || item.type != "user_defined";
    }

    if (cloning) {
      return item.type != "user_defined";
    }

    if (editMode === 'delete') {
      return !item.creator;
    }

    return false;
  }, [editMode, cloning, renaming, item]);

  const { isDragging, attributes, listeners, setNodeRef } = useDraggable({
    id: item.id || 'untitled',
    disabled: !!editMode || moving || item.id === 'uncategorized',
    data: {
      collection: item,
      type: 'collection'
    },
  });

  useEffect(() => {
    setUpdatedName(item.name);
  }, [renaming, item.name]);

  const handleClick = (e) => {
    e.preventDefault();

    if ((selection || renaming)) {
      if (onEditChanges && !disabled) {
        onEditChanges({
          id: item.id,
          type: 'collection',
          value: selected ? null : item
        });
      }
    } else if (onClick) {
      onClick(item);
    }
  }

  const handleRename = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUpdatedName(e.target.value);
    if (onEditChanges) {
      onEditChanges({
        id: item.id,
        type: 'collection',
        value: e.target.value
      });
    }
  }

  const handleTouchStart = () => {
    setWasTouchMoved(false);
  };
  
  const handleTouchMove = () => {
    setWasTouchMoved(true);
  };

  const handleTouchEnd = (e) => {
    e.preventDefault();
    if (!wasTouchMoved) {
      handleClick(e);
    }
  };


  return (
    <div className={cx(styles.card, {
      [styles.selected]: selected,
      [styles.readonly]: readonly,
      [styles.dragging]: isDragging,
    })}
      ref={setNodeRef} {...listeners} {...attributes} 
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      >
      <NavigateEntity
      path={`/collections/${item.id}`}
      onClick={handleClick}
      >
      <div className={styles.icon}>
        <Thumbnail media={icon}
          defaultImage={defaultImage}
          width={600}
        />

        {(internal || live || template) &&
          <div className={styles.indicators}>
            {internal &&
              <Tooltip content={'Internal Collection'} placement='right'>
                <i className="paperflite-lock"></i>
              </Tooltip>
            }
            {live &&
              <Tooltip content={'Live Collection'} placement='right'>
                <i className="fa-regular fa-circle-dot"></i>
              </Tooltip>
            }

            {template &&
              <Tooltip content={'Template Collection'} placement='right'>
                <i className="paperflite-categories"></i>
              </Tooltip>
            }
          </div>
        }

        {selection && !disabled &&
          <div className={styles.selection}>
            {selected && <i className="fa-solid fa-check-circle"></i>}
            {!selected && <i className="paperflite-radio-button-unselected"></i>}
          </div>
        }
      </div>
      <div className={styles.cardContent}>
      {(!renaming || disabled) &&
          <>
            <div className={styles.details}>
            <Tooltip content={name} placement="top">
              <div className={styles.name}>
              <LinesEllipsis className={styles.name} text={name} maxLine='2' />
              </div>
              </Tooltip>  
              <p className={styles.count}>{item.id !== 'uncategorized' ? count : <>&nbsp;</>}</p>
            </div>
            {!selection &&
              <a href={`/collections/${item.id}`} className={cx(styles.arrow)}>
                <i className="paperflite-arrow-up"></i>
              </a>
            }
          </>
        }

        {renaming && !disabled &&
          <div className={cx(styles.rename, { [styles.renamed]: renamed })}>
            <input type="text" value={updatedName} onChange={handleRename} />
          </div>
        }
      </div>
      </NavigateEntity>
    </div>
  );
}

const PlaceholderCollectionCard: React.FC<object> = () => {
  return (
    <div className={cx(styles.card, styles.placeholder)}>
      <div className={cx(styles.icon, styles.block)}></div>
      <div className={styles.cardContent}>
        <div className={styles.details}>
          <div className={cx(styles.name, styles.block)}></div>
          <p className={cx(styles.count, styles.block)}></p>
        </div>
      </div>
    </div>
  );
}

interface DraggableCollectionCardProps {
  item: Collection;
}

const DraggableCollectionCard: React.FC<DraggableCollectionCardProps> = ({ item, }) => {
  const name = _.get(item, 'name', '');
  const icon = _.get(item, 'banner.background') as Media;

  return (
    <div className={styles.draggableCard}>
      <Thumbnail className={styles.icon} media={icon}
        defaultImage='/images/collections/background.jpeg'
        width={100} />
      <div className={styles.name}>{name}</div>
    </div>
  );
}

export { CollectionCard, PlaceholderCollectionCard, DraggableCollectionCard };